import React, { ReactElement } from 'react';
import { SportsEnum, VenueAccessTypesEnum } from 'types/generated/client';
import { FilterDivider } from 'components/Explore/Lessons/LessonFiltersModal/LessonFilters';
import FilterBox from 'components/forms/FilterBox/FilterBox';
import { SliderNumberRange } from 'components/forms/Slider';
import Switch from 'components/forms/Switch/Switch';
import { CourtFiltersProps } from './props';
import {
  VENUE_ACCESS_OPTIONS,
  VENUE_COURT_TYPE_OPTIONS,
  VENUE_DISTANCE_IMPERIAL_OPTIONS,
  VENUE_NETS_OPTIONS,
  VENUE_SURFACE_OPTIONS,
} from './utils';

const SectionTitle = ({ children }: { children: React.ReactNode }): ReactElement => {
  return (
    <p className="typography-product-subheading mb-2 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
      {children}
    </p>
  );
};

const CourtFilters: React.FC<CourtFiltersProps> = ({
  setAccessType,
  accessType,
  setCourtType,
  courtType,
  nets,
  setNets,
  surface,
  setSurface,
  courtsMinNumber,
  courtsMaxNumber,
  setShowOnlyDedicatedCourts,
  showOnlyDedicatedCourts,
  setCourtsMaxNumber,
  setCourtsMinNumber,
  courtLimit,
  distance,
  setDistance,
  selectedSport,
}) => {
  // Tennis hides Dedicated Courts and Nets (Figma `Filters Courts` tennis variant):
  // "dedicated" is a pickleball concept, and tennis nets are always permanent.
  const isTennis = selectedSport === SportsEnum.Tennis;

  return (
    <div className="flex flex-auto flex-col gap-ds-xl overflow-y-auto px-4 py-6 md:px-ds-3xl">
      {/* Distance leads the modal (mobile design) and disappears at `md`, where the
          header's own distance dropdown takes over — see ExploreCourts.tsx. Every
          divider below is shown only when the section above it is also on screen, so
          no width ever opens with a stray hairline.

          Setting the radius is all these chips do — `useCourt` refetches from its
          `distance` effect, so calling the query here too would double-fetch. */}
      <div className="md:hidden">
        <SectionTitle>Distance</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {VENUE_DISTANCE_IMPERIAL_OPTIONS.map((newDistance) => (
            <FilterBox
              key={newDistance.id}
              label={newDistance.name}
              onClick={() => setDistance(newDistance)}
              isSelected={distance.id === newDistance.id}
            />
          ))}
        </div>
      </div>
      {/* Toggles are flush right on mobile and sit beside their label from `md` up —
          the two Figma variants differ on exactly this. */}
      {!isTennis && (
        <>
          <FilterDivider className="md:hidden" />
          <div className="flex items-center justify-between gap-ds-xl md:justify-start">
            <SectionTitle>Dedicated Courts</SectionTitle>
            <Switch
              isActive={showOnlyDedicatedCourts}
              toggleIsActive={() => setShowOnlyDedicatedCourts(!showOnlyDedicatedCourts)}
              className="!ml-0 mb-2 w-fit"
            />
          </div>
        </>
      )}
      {/* On tennis, Dedicated Courts is gone, so Free courts inherits its divider
          rule: only Distance sits above it, and Distance stops at `md`. */}
      <FilterDivider className={isTennis ? 'md:hidden' : undefined} />
      <div className="flex items-center justify-between gap-ds-xl md:justify-start">
        <SectionTitle>Free courts</SectionTitle>
        <Switch
          isActive={accessType.length === 1 && accessType[0] === VenueAccessTypesEnum.Free}
          toggleIsActive={() =>
            accessType.length === 1 && accessType[0] === VenueAccessTypesEnum.Free
              ? setAccessType([])
              : setAccessType([VenueAccessTypesEnum.Free])
          }
          className="!ml-0 mb-2 w-fit"
        />
      </div>
      <FilterDivider />
      <div className="block">
        <div className="mb-12">
          <SectionTitle>Number of courts</SectionTitle>
        </div>
        <SliderNumberRange
          rangeMinimum={1}
          rangeMaximum={courtLimit}
          valueMinimum={courtsMinNumber}
          valueMaximum={courtsMaxNumber}
          setValueMinumum={(newMinimum) => setCourtsMinNumber(newMinimum)}
          setValueMaximum={(newMaximum) => setCourtsMaxNumber(newMaximum)}
          step={1}
          // Courts are whole numbers — decimals={2} rendered the range as "1.00"/"40.00".
          decimals={0}
        />
      </div>
      <FilterDivider />
      <div>
        <SectionTitle>Access</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {VENUE_ACCESS_OPTIONS.map((access) => (
            <FilterBox
              key={access.id}
              label={access.name}
              onClick={() =>
                setAccessType((prev) => {
                  if (prev.includes(access.id)) {
                    return prev.filter((item) => item !== access.id);
                  }
                  return [...prev, access.id];
                })
              }
              isSelected={accessType.includes(access.id)}
            />
          ))}
        </div>
      </div>
      <FilterDivider />
      <div>
        <SectionTitle>Court type</SectionTitle>
        {/* Two full-width chips on mobile; a 3-column pitch with two filled above it. */}
        <div className="grid grid-cols-3 gap-4 max-sm:grid-cols-2">
          {VENUE_COURT_TYPE_OPTIONS.map((type) => (
            <FilterBox
              key={type.id}
              label={type.name}
              onClick={() =>
                setCourtType((prev) => {
                  if (prev.includes(type.id)) {
                    return prev.filter((item) => item !== type.id);
                  }
                  return [...prev, type.id];
                })
              }
              isSelected={courtType.includes(type.id)}
            />
          ))}
        </div>
      </div>
      {!isTennis && (
        <>
          <FilterDivider />
          <div>
            <SectionTitle>Nets</SectionTitle>
            <div className="grid grid-cols-3 gap-4">
              {VENUE_NETS_OPTIONS.map((net) => (
                <FilterBox
                  key={net.id}
                  label={net.name}
                  onClick={() =>
                    setNets((prev) => {
                      if (prev.includes(net.id)) {
                        return prev.filter((item) => item !== net.id);
                      }
                      return [...prev, net.id];
                    })
                  }
                  isSelected={nets.includes(net.id)}
                />
              ))}
            </div>
          </div>
        </>
      )}
      {/**
       * @note the lines data is bad. It says most lines are permanent, but our assumption is that this is including tennis lines
       */}
      {/* <div>
    <SectionTitle>Lines</SectionTitle>
    <div className="grid grid-cols-3 gap-4">
      {VENUE_LINES_OPTIONS.map((line) => {
        return (
          <FilterBox
            key={line.id}
            label={line.name}
            onClick={() => setLines(line.name)}
            isSelected={lines === line.name}
          />
        );
      })}
    </div>
  </div> */}
      <FilterDivider />
      <div>
        <SectionTitle>Surface</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {VENUE_SURFACE_OPTIONS.map((surfaces) => {
            return (
              <FilterBox
                key={surfaces.id}
                label={surfaces.name}
                onClick={() =>
                  setSurface((prev) => {
                    if (prev.includes(surfaces.id)) {
                      return prev.filter((item) => item !== surfaces.id);
                    }
                    return [...prev, surfaces.id];
                  })
                }
                isSelected={surface.includes(surfaces.id)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CourtFilters;
